import { supabase } from '../lib/supabase';

// Generate a random slug
const generateSlug = (length = 6) => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () => chars?.[Math.floor(Math.random() * chars?.length)])?.join('');
};

// Generate a 4-6 digit join code
const generateJoinCode = () => {
  return Math.floor(1000 + Math.random() * 9000)?.toString();
};

// ==================== STUDY NOTES ====================

export const studyNotesService = {
  // Get all notes for current user
  async getUserNotes(userId) {
    const { data, error } = await supabase?.from('study_notes')?.select('*, study_note_slides(id)')?.eq('created_by', userId)?.order('updated_at', { ascending: false });
    if (error) throw error;
    return data?.map(n => ({
      ...n,
      slideCount: n?.study_note_slides?.length || 0,
    })) || [];
  },

  // Get a single note by slug (public)
  async getNoteBySlug(slug) {
    const { data, error } = await supabase?.from('study_notes')?.select('*')?.eq('slug', slug)?.maybeSingle();
    if (error) throw error;
    return data;
  },

  // Get a single note by id
  async getNoteById(id) {
    const { data, error } = await supabase?.from('study_notes')?.select('*')?.eq('id', id)?.maybeSingle();
    if (error) throw error;
    return data;
  },

  // Check slug availability
  async isSlugAvailable(slug, excludeId = null) {
    let query = supabase?.from('study_notes')?.select('id')?.eq('slug', slug);
    if (excludeId) query = query?.neq('id', excludeId);
    const { data } = await query;
    return !data || data?.length === 0;
  },

  // Create a new note
  async createNote(noteData, userId) {
    let slug = noteData?.slug || generateSlug(6);
    const slugAvailable = await studyNotesService?.isSlugAvailable(slug);
    if (!slugAvailable) slug = generateSlug(8);

    const { data, error } = await supabase?.from('study_notes')?.insert({
        slug,
        title: noteData?.title,
        speaker: noteData?.speaker || null,
        description: noteData?.description || null,
        church_name: noteData?.churchName || null,
        default_version: noteData?.defaultVersion || 'KJV',
        sync_mode: noteData?.syncMode || 'self-paced',
        theme: noteData?.theme || 'light',
        font_size: noteData?.fontSize || 'medium',
        is_published: true,
        created_by: userId,
      })?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Update a note
  async updateNote(id, noteData) {
    const updatePayload = {};
    if (noteData?.title !== undefined) updatePayload.title = noteData?.title;
    if (noteData?.speaker !== undefined) updatePayload.speaker = noteData?.speaker;
    if (noteData?.description !== undefined) updatePayload.description = noteData?.description;
    if (noteData?.churchName !== undefined) updatePayload.church_name = noteData?.churchName;
    if (noteData?.defaultVersion !== undefined) updatePayload.default_version = noteData?.defaultVersion;
    if (noteData?.syncMode !== undefined) updatePayload.sync_mode = noteData?.syncMode;
    if (noteData?.theme !== undefined) updatePayload.theme = noteData?.theme;
    if (noteData?.fontSize !== undefined) updatePayload.font_size = noteData?.fontSize;
    if (noteData?.slug !== undefined) updatePayload.slug = noteData?.slug;

    const { data, error } = await supabase?.from('study_notes')?.update(updatePayload)?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Delete a note
  async deleteNote(id) {
    const { error } = await supabase?.from('study_notes')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

// ==================== SLIDES ====================

export const slidesService = {
  // Get all slides for a note
  async getSlides(noteId) {
    const { data, error } = await supabase?.from('study_note_slides')?.select('*')?.eq('note_id', noteId)?.order('position', { ascending: true });
    if (error) throw error;
    return data?.map(s => ({
      id: s?.id,
      noteId: s?.note_id,
      position: s?.position,
      slideType: s?.slide_type,
      verseReference: s?.verse_reference,
      textContent: s?.text_content,
      versionOverride: s?.version_override,
      slideTimerSeconds: s?.slide_timer_seconds,
    })) || [];
  },

  // Save all slides (upsert + delete removed)
  async saveSlides(noteId, slides) {
    // Delete existing slides
    await supabase?.from('study_note_slides')?.delete()?.eq('note_id', noteId);

    if (!slides || slides?.length === 0) return [];

    const insertData = slides?.map((s, idx) => ({
      note_id: noteId,
      position: idx,
      slide_type: s?.slideType || 'text',
      verse_reference: s?.verseReference || null,
      text_content: s?.textContent || null,
      version_override: s?.versionOverride || null,
      slide_timer_seconds: s?.slideTimerSeconds || null,
    }));

    const { data, error } = await supabase?.from('study_note_slides')?.insert(insertData)?.select();
    if (error) throw error;
    return data || [];
  },
};

// ==================== SESSIONS ====================

export const sessionsService = {
  // Get or create session for a note
  async getOrCreateSession(noteId) {
    const { data: existing } = await supabase?.from('study_note_sessions')?.select('*')?.eq('note_id', noteId)?.order('started_at', { ascending: false })?.limit(1)?.maybeSingle();

    if (existing) return existing;

    const { data, error } = await supabase?.from('study_note_sessions')?.insert({
        note_id: noteId,
        current_slide_index: 0,
        is_live: false,
        qa_enabled: true,
        qa_join_code: generateJoinCode(),
        qa_moderation: false,
        qa_members_only: false,
      })?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Update session (slide index, live status, qa settings)
  async updateSession(sessionId, updates) {
    const { data, error } = await supabase?.from('study_note_sessions')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', sessionId)?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Regenerate join code
  async regenerateJoinCode(sessionId) {
    const newCode = generateJoinCode();
    const { data, error } = await supabase?.from('study_note_sessions')?.update({ qa_join_code: newCode, updated_at: new Date()?.toISOString() })?.eq('id', sessionId)?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Subscribe to session changes (realtime) - uses both broadcast and postgres_changes for reliability
  subscribeToSession(sessionId, callback) {
    const channel = supabase?.channel(`session-sync:${sessionId}`, {
      config: { broadcast: { self: false } },
    });

    // Listen for broadcast events (sent by presenter for instant sync)
    channel?.on('broadcast', { event: 'slide_change' }, (payload) => {
      if (payload?.payload) callback(payload?.payload);
    });

    // Also listen for postgres_changes as fallback
    channel?.on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'study_note_sessions',
      filter: `id=eq.${sessionId}`,
    }, (payload) => {
      if (payload?.new) callback(payload?.new);
    });

    channel?.subscribe();
    return channel;
  },

  // Broadcast slide change to audience (called by presenter)
  async broadcastSlideChange(sessionId, slideIndex, isLive) {
    const channel = supabase?.channel(`session-sync:${sessionId}`);
    await channel?.send({
      type: 'broadcast',
      event: 'slide_change',
      payload: { current_slide_index: slideIndex, is_live: isLive, id: sessionId },
    });
  },
};

// ==================== COMMENTS ====================

export const commentsService = {
  // Get comments for a session
  async getComments(sessionId) {
    const { data, error } = await supabase?.from('study_note_comments')?.select('*')?.eq('session_id', sessionId)?.eq('is_deleted', false)?.eq('is_approved', true)?.order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  // Post a comment
  async postComment({ noteId, sessionId, slideIndex, userId, displayName, content }) {
    const { data, error } = await supabase?.from('study_note_comments')?.insert({
        note_id: noteId,
        session_id: sessionId,
        slide_index: slideIndex || null,
        user_id: userId || null,
        display_name: displayName || 'Anonymous',
        content,
        is_approved: true,
        is_deleted: false,
      })?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Delete a comment (presenter)
  async deleteComment(commentId) {
    const { error } = await supabase?.from('study_note_comments')?.update({ is_deleted: true })?.eq('id', commentId);
    if (error) throw error;
  },

  // Subscribe to new comments
  subscribeToComments(sessionId, callback) {
    return supabase?.channel(`comments:${sessionId}`)?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'study_note_comments',
        filter: `session_id=eq.${sessionId}`,
      }, (payload) => callback(payload?.new))?.subscribe();
  },
};

// ==================== REACTIONS ====================

export const reactionsService = {
  // Get reaction counts for a session
  async getReactionCounts(sessionId) {
    const { data, error } = await supabase?.from('study_note_reactions')?.select('reaction_type')?.eq('session_id', sessionId);
    if (error) return { thumbs_up: 0, pray: 0, cross: 0 };

    const counts = { thumbs_up: 0, pray: 0, cross: 0 };
    data?.forEach(r => { if (counts?.[r?.reaction_type] !== undefined) counts[r.reaction_type]++; });
    return counts;
  },

  // Add a reaction
  async addReaction({ noteId, sessionId, reactionType, fingerprint }) {
    const { error } = await supabase?.from('study_note_reactions')?.insert({
        note_id: noteId,
        session_id: sessionId,
        reaction_type: reactionType,
        user_fingerprint: fingerprint || null,
      });
    if (error) throw error;
  },

  // Subscribe to reactions
  subscribeToReactions(sessionId, callback) {
    return supabase?.channel(`reactions:${sessionId}`)?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'study_note_reactions',
        filter: `session_id=eq.${sessionId}`,
      }, (payload) => callback(payload?.new))?.subscribe();
  },
};

export { generateSlug, generateJoinCode };
